import {
  ActionIcon,
  Box,
  Group,
  Loader,
  Menu,
  Paper,
  SimpleGrid,
  Text,
  Stack,
  Badge,
} from '@mantine/core';
import { IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  AllergyIntolerance,
  AllergyIntoleranceReaction,
} from '@medplum/fhirtypes';
import { useDateDisplay } from '@hooks/useDateDisplay';
import {
  getAllergyCodeText,
  getCategory,
  getClinicalStatus,
  getOnsetDate,
  getReactions,
  getReactionManifestation,
  getReactionSeverity,
  getStatusColor,
} from '@/utils/allergy.utils';
import { LabeledField } from '@/components/LabeledField';

export type AllergyAction = 'edit' | 'remove';

/**
 * Component to display a single allergy reaction
 */
interface ReactionDisplayProps {
  reaction: AllergyIntoleranceReaction;
  index: number;
}

const ReactionDisplay = ({ reaction, index }: ReactionDisplayProps) => {
  const { t } = useTranslation(['patient-pages']);
  const manifestation = getReactionManifestation(reaction);
  const severity = getReactionSeverity(reaction);
  const manifestationDisplay =
    manifestation?.text || manifestation?.coding?.[0]?.display;
  const severityDisplay = severity?.display || severity?.code;

  return (
    <Box pl="xs">
      <LabeledField
        label={`${t('summaryPage.summary.allergies.dialog.reaction', {
          ns: 'patient-pages',
          defaultValue: 'Reaction',
        })} ${index + 1}`}
        value={
          manifestationDisplay && severityDisplay
            ? `${manifestationDisplay} (${severityDisplay})`
            : manifestationDisplay || severityDisplay || '-'
        }
      />
    </Box>
  );
};

export interface AllergyListItemProps {
  allergy: AllergyIntolerance;
  onAction: (action: AllergyAction) => void;
  isDeleting?: boolean;
  hideActions?: boolean;
}

export const AllergyListItem = ({
  allergy,
  onAction,
  isDeleting = false,
  hideActions = false,
}: AllergyListItemProps) => {
  const { t } = useTranslation(['patient-pages', 'common']);
  const formatDate = useDateDisplay();

  // Use utility functions for safe nested access
  const allergyName =
    getAllergyCodeText(allergy) ||
    t('summaryPage.summary.allergies.unknownAllergy', {
      ns: 'patient-pages',
      defaultValue: 'Unknown Allergy',
    });

  // Extract individual fields
  const category = getCategory(allergy);
  const categoryDisplay = category?.[0]
    ? t(`summaryPage.summary.allergies.dialog.categoryOptions.${category[0]}`, {
        ns: 'patient-pages',
        defaultValue: category[0],
      })
    : undefined;

  const clinicalStatus = getClinicalStatus(allergy);
  const statusCode = clinicalStatus?.coding?.[0]?.code;
  const clinicalStatusDisplay = statusCode
    ? t(`summaryPage.summary.allergies.statusOptions.${statusCode}`, {
        ns: 'patient-pages',
        defaultValue:
          clinicalStatus?.text || clinicalStatus?.coding?.[0]?.display || '',
      })
    : clinicalStatus?.text || clinicalStatus?.coding?.[0]?.display;

  const reactions = getReactions(allergy);
  const onsetDate = getOnsetDate(allergy);

  // Determine which actions are available
  // - Edit: available for all allergies (unless deleting)
  // - Remove: available for all allergies (unless deleting)
  const canEdit = !isDeleting;
  const canRemove = !isDeleting;
  const hasActions = !hideActions && (canEdit || canRemove);

  return (
    <Paper p="sm" withBorder radius="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Box flex={1}>
          {/* Allergy name */}
          <Text fw={600} size="sm" mb={8}>
            {allergyName}
          </Text>

          {/* Fields in 2-column grid */}
          <SimpleGrid cols={2} spacing="xs" verticalSpacing={4}>
            <LabeledField
              label={t('summaryPage.summary.allergies.dialog.category', {
                ns: 'patient-pages',
              })}
              value={categoryDisplay}
            />
            <LabeledField
              label={t('summaryPage.summary.allergies.dialog.onsetDate', {
                ns: 'patient-pages',
              })}
              value={formatDate(onsetDate)}
            />
          </SimpleGrid>

          {/* Reactions */}
          {reactions.length > 0 && (
            <Box mt={8}>
              <Text size="xs" fw={500} c="dark" mb={4}>
                {t('summaryPage.summary.allergies.dialog.reactions', {
                  ns: 'patient-pages',
                  defaultValue: 'Reactions',
                })}
              </Text>
              <Stack gap={4}>
                {reactions.map((reaction, index) => (
                  <ReactionDisplay
                    key={index}
                    reaction={reaction}
                    index={index}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        {clinicalStatus && (
          <Badge
            color={getStatusColor(clinicalStatus.coding?.[0]?.code || '')}
            variant="light"
          >
            {clinicalStatusDisplay}
          </Badge>
        )}

        {/* Actions menu or loading indicator */}
        {isDeleting ? (
          <Loader size="sm" />
        ) : (
          hasActions && (
            <Menu shadow="md" width={160} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                {canEdit && (
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => onAction('edit')}
                  >
                    {t('edit', {
                      ns: 'common',
                      defaultValue: 'Edit',
                    })}
                  </Menu.Item>
                )}
                {canRemove && (
                  <Menu.Item
                    leftSection={<IconTrash size={14} />}
                    onClick={() => onAction('remove')}
                    color="red"
                  >
                    {t('delete', {
                      ns: 'common',
                      defaultValue: 'Delete',
                    })}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )
        )}
      </Group>
    </Paper>
  );
};
